// ABOUTME: Search-table cell renderer for link contents: the linked nodes labelled by their listName, else their code.
// ABOUTME: Same look and click as the package's link cell; the values stay codes, the names come from the page index.

import type { CustomCellRendererProps, NodeContentValueO } from '@ds/data-table';
import type { MutableRefObject, ReactElement } from 'react';
import { createContext, useContext } from 'react';

import { LinkListNamesIndex, resolveLinkLabel } from '../linkListNames';
import { LinkChipClickHandler, LinkChipList } from './LinkChipList';

/**
 * The link listNames of the current page, provided by DataTableWrapper. A ref, read at render: the
 * renderers map handed to the package must stay module-scoped, so the index cannot reach the
 * cells through props.
 */
export const LinkListNamesContext = createContext<MutableRefObject<LinkListNamesIndex> | null>(
    null,
);

export default function GridCellLinkRender(props: CustomCellRendererProps) {
    const { value, row, ct, disabled, onLinkClick, DefaultRenderer } = props;
    const linkListNamesRef = useContext(LinkListNamesContext);
    const hierarchy = ct?.hierarchy;

    if (!hierarchy) {
        // Called, not mounted: the package creates `DefaultRenderer` anew on every render.
        return (DefaultRenderer as () => ReactElement)();
    }

    // An empty link value is no link: with none left the cell stays empty instead of showing a
    // blank chip.
    const links: string[] = ((value as NodeContentValueO[] | undefined)?.[0]?.value ?? [])
        .filter((link: unknown) => link !== null && link !== undefined && String(link) !== '')
        .map((link: unknown) => String(link));
    const index = linkListNamesRef?.current;

    const handleClick: LinkChipClickHandler = (e, i) => {
        e.stopPropagation();
        onLinkClick?.(hierarchy, links[i]);
    };

    return (
        <LinkChipList
            items={links.map((link, i) => ({
                key: `${i}-${link}`,
                label: resolveLinkLabel(index, row.id, hierarchy, link),
            }))}
            onItemClick={handleClick}
            disabled={disabled}
        />
    );
}
